import React, { useState, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList, FileText, Users, AlertTriangle, Package,
  Upload, CheckCircle2, ArrowLeft, Download, BarChart3
} from 'lucide-react';

const REPORTS = [
  { id: 'defect-register', label: 'Defect Register', desc: 'Full list of all defects/snags with status', icon: FileText, color: 'text-blue-600 bg-blue-50' },
  { id: 'by-subcontractor', label: 'By Subcontractor', desc: 'Defect counts grouped by assigned company', icon: Users, color: 'text-purple-600 bg-purple-50' },
  { id: 'completion-blockers', label: 'Completion Blockers', desc: 'Items blocking completion/handover', icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
  { id: 'handover-readiness', label: 'Handover Readiness', desc: 'Package readiness with defect resolution rates', icon: Package, color: 'text-green-600 bg-green-50' },
  { id: 'consultant-uploads', label: 'Consultant Uploads', desc: 'AI extraction conversion tracking', icon: Upload, color: 'text-amber-600 bg-amber-50' },
  { id: 'outstanding-documents', label: 'Outstanding Documents', desc: 'Close-out documents not yet approved', icon: CheckCircle2, color: 'text-teal-600 bg-teal-50' },
];

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700', assigned: 'bg-blue-100 text-blue-700',
  acknowledged: 'bg-indigo-100 text-indigo-700', in_progress: 'bg-cyan-100 text-cyan-700',
  rectified_by_subcontractor: 'bg-teal-100 text-teal-700', closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', deferred: 'bg-slate-100 text-slate-600',
  required: 'bg-amber-100 text-amber-700', submitted: 'bg-purple-100 text-purple-700',
  approved: 'bg-green-100 text-green-700',
};

export default function CloseoutReports({ projectId, settings }) {
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async (reportId) => {
    setLoading(true);
    setActiveReport(reportId);
    try {
      const apiMap = {
        'defect-register': () => api.closeout.reportDefectRegister(projectId),
        'by-subcontractor': () => api.closeout.reportBySubcontractor(projectId),
        'completion-blockers': () => api.closeout.reportBlockers(projectId),
        'handover-readiness': () => api.closeout.reportHandover(projectId),
        'consultant-uploads': () => api.closeout.reportUploads(projectId),
        'outstanding-documents': () => api.closeout.reportOutstandingDocs(projectId),
      };
      const res = await apiMap[reportId]();
      setReportData(res.data);
    } catch { toast.error('Failed to load report'); } finally { setLoading(false); }
  }, [projectId]);

  if (!activeReport) {
    return (
      <div className="space-y-4" data-testid="closeout-reports">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Close-Out Reports</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORTS.map(r => {
            const Icon = r.icon;
            return (
              <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => loadReport(r.id)} data-testid={`report-${r.id}`}>
                <CardContent className="p-5">
                  <div className={`inline-flex p-2.5 rounded-lg ${r.color} mb-3`}><Icon className="h-5 w-5" /></div>
                  <h3 className="text-sm font-bold text-slate-800">{r.label}</h3>
                  <p className="text-xs text-slate-500 mt-1">{r.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  const report = REPORTS.find(r => r.id === activeReport);

  return (
    <div className="space-y-4" data-testid="closeout-reports">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setActiveReport(null); setReportData(null); }} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">{report?.label}</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>
      ) : (
        <ReportContent reportId={activeReport} data={reportData} settings={settings} />
      )}
    </div>
  );
}

function ReportContent({ reportId, data, settings }) {
  if (!data) return null;

  switch (reportId) {
    case 'defect-register':
      return <DefectRegisterReport data={data} />;
    case 'by-subcontractor':
      return <SubcontractorReport data={data} />;
    case 'completion-blockers':
      return <BlockersReport data={data} settings={settings} />;
    case 'handover-readiness':
      return <HandoverReport data={data} />;
    case 'consultant-uploads':
      return <UploadsReport data={data} />;
    case 'outstanding-documents':
      return <OutstandingDocsReport data={data} />;
    default:
      return null;
  }
}

function DefectRegisterReport({ data }) {
  const items = data.items || [];
  return (
    <div>
      <Card className="mb-4"><CardContent className="p-3"><p className="text-sm"><span className="font-bold">{data.total}</span> defects in register</p></CardContent></Card>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="report-defect-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Ref</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Description</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Location</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Trade</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Severity</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Company</th>
              <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d.id} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{d.defect_no}</td>
                <td className="px-3 py-2 max-w-[200px]"><p className="truncate text-xs">{d.description_short}</p></td>
                <td className="px-3 py-2 text-xs text-slate-600">{d.location_text || '-'}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{d.trade_code || '-'}</Badge></td>
                <td className="px-3 py-2"><Badge className={`text-[10px] ${d.severity === 'blocker' ? 'bg-red-100 text-red-800' : d.severity === 'major' ? 'bg-orange-100 text-orange-800' : 'bg-slate-100 text-slate-600'}`}>{d.severity}</Badge></td>
                <td className="px-3 py-2 text-xs">{d.assigned_company || '-'}</td>
                <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubcontractorReport({ data }) {
  const companies = data.companies || {};
  return (
    <div className="space-y-3">
      {Object.entries(companies).map(([company, stats]) => (
        <Card key={company} data-testid={`sub-report-${company}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">{company}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{stats.total} total defects</p>
              </div>
              <div className="flex gap-4 text-center">
                <div><p className="text-lg font-bold text-red-600">{stats.open}</p><p className="text-[10px] text-slate-400">Open</p></div>
                <div><p className="text-lg font-bold text-green-600">{stats.closed}</p><p className="text-[10px] text-slate-400">Closed</p></div>
                <div><p className="text-lg font-bold text-amber-600">{stats.overdue}</p><p className="text-[10px] text-slate-400">Overdue</p></div>
              </div>
            </div>
            {stats.total > 0 && (
              <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(stats.closed / stats.total * 100).toFixed(0)}%` }} />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {Object.keys(companies).length === 0 && (
        <Card><CardContent className="p-12 text-center"><Users className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No subcontractor assignments yet</p></CardContent></Card>
      )}
    </div>
  );
}

function BlockersReport({ data, settings }) {
  const profile = settings?.profile || {};
  const completionTerm = profile.completion_term || 'Completion';

  return (
    <div className="space-y-4">
      <Card className="border-red-200"><CardContent className="p-4">
        <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /><span className="font-bold">{data.total} total blockers</span> preventing {completionTerm}</div>
      </CardContent></Card>

      {data.defect_blockers?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-3">Defect Blockers ({data.defect_blockers.length})</h3>
            {data.defect_blockers.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-2 border-b last:border-b-0">
                <span className="font-mono text-xs text-slate-400">{d.defect_no}</span>
                <span className="text-sm flex-1">{d.description_short}</span>
                <Badge className="text-[10px] bg-red-100 text-red-700">{d.severity}</Badge>
                <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.document_blockers?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-3">Document Blockers ({data.document_blockers.length})</h3>
            {data.document_blockers.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-2 border-b last:border-b-0">
                <FileText className="h-4 w-4 text-slate-400" />
                <span className="text-sm flex-1">{d.document_type}</span>
                {d.required_from && <span className="text-xs text-slate-500">From: {d.required_from}</span>}
                <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.signoff_blockers?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-3">Sign-Off Blockers ({data.signoff_blockers.length})</h3>
            {data.signoff_blockers.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-2 border-b last:border-b-0">
                <CheckCircle2 className="h-4 w-4 text-slate-400" />
                <span className="text-sm flex-1">{s.signoff_type}</span>
                {s.required_role && <span className="text-xs text-slate-500">Role: {s.required_role}</span>}
                <Badge className={`text-[10px] ${STATUS_COLORS[s.status] || 'bg-slate-100'}`}>{s.status?.replace(/_/g, ' ')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HandoverReport({ data }) {
  const packages = data.packages || [];
  return (
    <div className="space-y-3">
      {packages.map(pkg => (
        <Card key={pkg.id} data-testid={`handover-pkg-${pkg.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-800">{pkg.package_name}</h3>
                <p className="text-xs text-slate-500">{pkg.package_type?.replace(/_/g, ' ')} | {pkg.area_reference || pkg.area_type}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{pkg.defect_pct}%</p>
                <p className="text-[10px] text-slate-500">Defects Resolved</p>
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pkg.defect_pct >= 95 ? 'bg-green-500' : pkg.defect_pct >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pkg.defect_pct}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-1">{pkg.defect_closed}/{pkg.defect_total} defects closed</p>
          </CardContent>
        </Card>
      ))}
      {packages.length === 0 && (
        <Card><CardContent className="p-12 text-center"><Package className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No packages found</p></CardContent></Card>
      )}
    </div>
  );
}

function UploadsReport({ data }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{data.total}</p><p className="text-[10px] text-slate-500 uppercase">Total Uploads</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{data.processed}</p><p className="text-[10px] text-slate-500 uppercase">Processed</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{data.pending}</p><p className="text-[10px] text-slate-500 uppercase">Pending</p></CardContent></Card>
      </div>
      {(data.uploads || []).map(u => (
        <Card key={u.id}>
          <CardContent className="p-3 flex items-center gap-3">
            <Upload className="h-4 w-4 text-slate-400" />
            <div className="flex-1">
              <p className="text-sm font-medium">{u.consultant_company || 'Upload'} — {u.discipline || 'General'}</p>
              <p className="text-xs text-slate-500">{u.extracted_count || 0} items extracted | Round {u.round_no || 1}</p>
            </div>
            <Badge className={`text-[10px] ${u.ai_status === 'items_created' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{u.ai_status?.replace(/_/g, ' ')}</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OutstandingDocsReport({ data }) {
  const items = data.items || [];
  return (
    <div>
      <Card className="mb-4"><CardContent className="p-3"><p className="text-sm"><span className="font-bold">{data.total}</span> outstanding documents</p></CardContent></Card>
      {items.map(doc => (
        <Card key={doc.id} className="mb-2">
          <CardContent className="p-3 flex items-center gap-3">
            <FileText className="h-4 w-4 text-slate-400" />
            <div className="flex-1">
              <p className="text-sm font-medium">{doc.document_type}</p>
              <p className="text-xs text-slate-500">{doc.document_group} {doc.required_from_company ? `| From: ${doc.required_from_company}` : ''}</p>
            </div>
            {doc.mandatory_flag && <Badge className="bg-red-50 text-red-600 text-[9px]">Mandatory</Badge>}
            <Badge className={`text-[10px] ${STATUS_COLORS[doc.status] || 'bg-slate-100'}`}>{doc.status?.replace(/_/g, ' ')}</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
